import { api } from './api';

/**
 * Images on scratchpad tasks and manual scope todos.
 *
 * Two things here are not obvious. Uploads must clear the axios instance's
 * default JSON Content-Type, and viewing an image goes through a signed URL that
 * is deliberately short-lived, so the URL is fetched when the image is shown
 * rather than cached alongside the record.
 */

// Kept in step with ALLOWED_TYPES and MAX_BYTES on the server. The server is the
// one that enforces them; these exist so the browser can refuse early and say
// something useful instead of round-tripping to a 413.
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Screenshots come off high-density displays far larger than anything that gets
// looked at in a 200px thumbnail or a lightbox, and the same file is what lands
// in Jira. Downscaling here keeps the upload small without a native image
// library on a 512MB server instance.
const MAX_EDGE = 2000;
const RE_ENCODE_QUALITY = 0.85;

/**
 * axios is configured app-wide with `Content-Type: application/json`. Sending a
 * FormData under that header means the browser never writes the multipart
 * boundary, and the request arrives unparseable. Setting it to undefined puts
 * the browser back in charge of the header.
 */
const MULTIPART = { headers: { 'Content-Type': undefined } };

function isAnimated(file) {
  // GIFs may be animated, and drawing one to a canvas silently keeps only the
  // first frame. Better to upload it untouched and let the size cap judge it.
  return file.type === 'image/gif';
}

/** Read a File into an HTMLImageElement so it can be measured and drawn. */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('Could not process the image.'))),
      type,
      quality
    );
  });
}

/**
 * Shrink an image to something reasonable to store and to attach, and report its
 * dimensions so a thumbnail can hold its space before the signed URL resolves.
 *
 * Falls back to the original file whenever processing would not help or cannot
 * be trusted: an animated GIF, an image already small enough, or any failure in
 * the canvas path. The server's cap is the real gate, so a passthrough that is
 * too large is refused there rather than here.
 *
 * @returns {Promise<{file: File|Blob, width: number|null, height: number|null}>}
 */
export async function prepareImage(file) {
  if (isAnimated(file)) {
    return { file, width: null, height: null };
  }

  try {
    const img = await loadImage(file);
    const { naturalWidth: w, naturalHeight: h } = img;
    const longest = Math.max(w, h);

    if (longest <= MAX_EDGE && file.size <= MAX_IMAGE_BYTES) {
      return { file, width: w, height: h };
    }

    const scale = Math.min(1, MAX_EDGE / longest);
    const width = Math.max(1, Math.round(w * scale));
    const height = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);

    // PNG screenshots re-encode enormously better as JPEG, but transparency is
    // worth keeping when it is there, and PNG is the only one of the two that
    // has it. Losing it would visibly change what the person attached.
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, type, RE_ENCODE_QUALITY);

    // Re-encoding can come out larger than the original on small or already
    // optimized files. Keep whichever is smaller.
    if (blob.size >= file.size && longest <= MAX_EDGE) {
      return { file, width: w, height: h };
    }

    const name = file.name || `image.${type === 'image/png' ? 'png' : 'jpg'}`;
    return {
      file: new File([blob], name, { type }),
      width,
      height,
    };
  } catch {
    return { file, width: null, height: null };
  }
}

/** A filename for a pasted screenshot, which arrives with none of its own. */
export function pastedImageName(type) {
  const ext = (type || '').split('/')[1] || 'png';
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `pasted-${stamp}.${ext}`;
}

async function uploadTo(path, file, { width, height } = {}) {
  const form = new FormData();
  form.append('file', file, file.name);
  if (width) form.append('width', String(width));
  if (height) form.append('height', String(height));

  const res = await api.post(path, form, MULTIPART);
  return res.data;
}

/**
 * Attach an image to a scratchpad task.
 * @param {string} taskId
 * @param {File|Blob} file
 * @param {Object} [dimensions] - { width, height } from prepareImage
 */
export const uploadTaskAttachment = async (taskId, file, dimensions) =>
  uploadTo(`/shape-up/attachments/bet-tasks/${taskId}`, file, dimensions);

/**
 * Attach an image to a manual scope todo.
 * @param {string} todoId
 * @param {File|Blob} file
 * @param {Object} [dimensions] - { width, height } from prepareImage
 */
export const uploadTodoAttachment = async (todoId, file, dimensions) =>
  uploadTo(`/shape-up/attachments/scope-todos/${todoId}`, file, dimensions);

/**
 * The images on a scratchpad task. They also ride along in the hill and bet
 * payloads; this is for refetching after a change.
 * @param {string} taskId
 */
export const getTaskAttachments = async (taskId) => {
  const res = await api.get(`/shape-up/attachments/bet-tasks/${taskId}`);
  return res.data;
};

/**
 * The images on a manual scope todo.
 * @param {string} todoId
 */
export const getTodoAttachments = async (todoId) => {
  const res = await api.get(`/shape-up/attachments/scope-todos/${todoId}`);
  return res.data;
};

/**
 * A short-lived signed URL for displaying an image. Fetched at display time
 * because it expires; see expiresInSeconds on the response.
 * @param {string} attachmentId
 */
export const getAttachmentUrl = async (attachmentId) => {
  const res = await api.get(`/shape-up/attachments/${attachmentId}/url`);
  return res.data;
};

/**
 * Remove an image.
 * @param {string} attachmentId
 */
export const deleteAttachment = async (attachmentId) => {
  const res = await api.delete(`/shape-up/attachments/${attachmentId}`);
  return res.data;
};

/**
 * Pull image files out of a paste event. Returns [] for an ordinary text paste,
 * which is the common case and must not be disturbed.
 * @param {ClipboardEvent} event
 */
export function imagesFromClipboard(event) {
  const items = Array.from(event.clipboardData?.items || []);
  return items
    .filter(item => item.kind === 'file' && ACCEPTED_IMAGE_TYPES.includes(item.type))
    .map(item => item.getAsFile())
    .filter(Boolean);
}

/** Turn a server error into something worth showing a person. */
export function describeUploadError(error) {
  return error?.response?.data?.message || 'Could not attach the image.';
}
